import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import {
  getNotification,
  getNotificationConfig,
  getNotificationSummary,
  getNewNotifications,
  listNotifications,
  markAllNotificationsRead,
  setNotificationArchived,
  setNotificationRead,
  type ListParams,
} from '../lib/notificationApi';
import type { NotificationItem, NotificationPage } from '../types/notification';

export const notificationKeys = {
  config: ['notification-config'] as const,
  summary: (userId: string | number | null) => ['notification-summary', userId] as const,
  list: (userId: string | number | null, filters: ListParams) =>
    ['notifications', userId, filters] as const,
};

export function useNotificationConfig() {
  return useQuery({
    queryKey: notificationKeys.config,
    queryFn: getNotificationConfig,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

export function useNotificationSummary(userId: string | number | null, enabled: boolean) {
  return useQuery({
    queryKey: notificationKeys.summary(userId),
    queryFn: getNotificationSummary,
    enabled: enabled && userId != null,
    refetchOnWindowFocus: true,
  });
}

export function useNotificationList(
  userId: string | number | null,
  filters: ListParams,
  enabled: boolean,
) {
  return useInfiniteQuery({
    queryKey: notificationKeys.list(userId, filters),
    queryFn: ({ pageParam }) =>
      listNotifications({ ...filters, cursor: (pageParam as string | null) ?? null }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage?.nextCursor ?? undefined,
    enabled: enabled && userId != null,
  });
}

/** Flattened, de-duplicated rows in server order across every loaded page. */
export function flattenNotificationPages(
  pages: NotificationPage[] | undefined,
): NotificationItem[] {
  if (!pages) return [];
  const seen = new Set<string>();
  const items: NotificationItem[] = [];
  for (const page of pages) {
    for (const item of page.items ?? []) {
      if (seen.has(item.id)) continue;
      seen.add(item.id);
      items.push(item);
    }
  }
  return items;
}

/**
 * Resolves a notification id that may sit outside the currently loaded pages.
 *
 * Walks the raw query cache instead of `getQueriesData({ queryKey: [...] })`:
 * a partial-key match hashes the filter object and does not always match the key
 * the page actually stored, which would silently miss the cached row and turn a
 * cheap lookup into a network round trip.
 */
export async function resolveNotificationById(
  queryClient: Pick<QueryClient, 'getQueryCache' | 'getQueryData'>,
  userId: string | number | null,
  id: string,
): Promise<NotificationItem | null> {
  for (const query of queryClient.getQueryCache().getAll()) {
    const key = query.queryKey;
    if (!Array.isArray(key) || key[0] !== 'notifications' || key[1] !== userId) continue;
    const data = queryClient.getQueryData<{ pages?: NotificationPage[] }>(key);
    const found = flattenNotificationPages(data?.pages).find((item) => item.id === id);
    if (found) return found;
  }
  try {
    return await getNotification(id);
  } catch {
    return null;
  }
}

export function useNewNotifications() {
  return useQuery({
    queryKey: ['notifications-new'],
    queryFn: () => getNewNotifications({ afterCreatedRevision: '0' }),
    enabled: false,
  });
}

export function useNotificationMutations(userId: string | number | null) {
  const queryClient = useQueryClient();
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: notificationKeys.summary(userId) });
    queryClient.invalidateQueries({ queryKey: ['notifications', userId] });
  };

  const setRead = useMutation({
    mutationFn: ({ id, read }: { id: string; read: boolean }) => setNotificationRead(id, read),
    onSuccess: invalidate,
  });
  const markAllRead = useMutation({
    mutationFn: (throughCreatedRevision: string) => markAllNotificationsRead(throughCreatedRevision),
    onSuccess: invalidate,
  });
  const setArchived = useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      setNotificationArchived(id, archived),
    onSuccess: invalidate,
  });

  return { setRead, markAllRead, setArchived };
}
